import { useState } from "react";
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import { Button, Panel } from "@crashx/ui";
import type { BetPanel as BetPanelId } from "@crashx/shared";
import { selectConfig, selectPhase, useRoundStore, selectBalance } from "../../state/roundStore.js";
import { selectCanBet, useConnectionStore } from "../../state/connectionStore.js";
import { useBetActions } from "./useBetActions.js";
import { useAutoBet } from "./useAutoBet.js";
import { AutoBetAction } from "./AutoBetAction.js";
import { BetFooter } from "./BetFooter.js";
import { AutoBetControls, useAutoSettings } from "./AutoBetControls.js";
import { BetAmount } from "./BetAmount.js";
import { validBetSettings } from "./betSettings.js";
import { LiveWin } from "./LiveWin.js";
export function BetPanel({ panel }: { panel: BetPanelId }) {
  const { t } = useTranslation();
  const config = useRoundStore(selectConfig);
  const phase = useRoundStore(selectPhase);
  const canBet = useConnectionStore(selectCanBet);
  const currency = config?.currencies[0];
  const balance = useRoundStore(selectBalance(currency?.code ?? ""));
  const actions = useBetActions(panel);
  const { activeBet, pending, error } = actions;
  const [amount, setAmount] = useState("1");
  const [autoCashout, setAutoCashout] = useState("");
  const [tab, setTab] = useState<"bet" | "auto">("bet");
  const fields = useAutoSettings();
  const auto = useAutoBet(panel, amount, autoCashout, actions.place);
  const latest = useRoundStore((state) => state.myBets.filter((bet) => bet.panel === panel).at(-1));
  const valid = validBetSettings(
    amount,
    autoCashout,
    currency,
    balance?.balance ?? null,
    config?.maxMultiplier ?? 1,
  );
  const decimals = currency?.decimals ?? 2;
  const blocked = pending || activeBet !== null || auto.running;
  const place = () => {
    if (!currency || !valid) return;
    void actions.place(
      new Decimal(amount).toNumber(),
      currency.code,
      autoCashout === "" ? null : Number(autoCashout),
    );
  };
  return (
    <section aria-label={t("bet.panel", { panel })}>
      <Panel className="cx-bet-panel">
        <div className="cx-bet-panel-heading">
          <strong>{t("bet.panel", { panel })}</strong>
          {balance ? (
            <span>
              {t("balance.label")}: {new Decimal(balance.balance).toFixed(decimals)} {balance.currency}
            </span>
          ) : null}
        </div>
        <div role="group" aria-label={t("bet.mode")} className="cx-bet-mode-tabs">
          {(["bet", "auto"] as const).map((id) => (
            <Button
              key={id}
              variant={tab === id ? "primary" : "ghost"}
              aria-pressed={tab === id}
              onClick={() => {
                if (id === "bet") auto.stop();
                setTab(id);
              }}
            >
              {t(`bet.tabs.${id}`)}
            </Button>
          ))}
        </div>
        <div className="cx-bet-panel-body">
          <div className="cx-bet-panel-inputs">
            {currency ? (
              <BetAmount value={amount} onChange={setAmount} currency={currency} disabled={blocked} />
            ) : (
              <p role="status">{t("app.loading")}</p>
            )}
          </div>
          <div className="cx-bet-action-column">
            {tab === "auto" ? (
              <AutoBetAction
                auto={auto}
                settings={fields.settings}
                valid={fields.valid}
                disabled={!canBet || !valid || pending || activeBet !== null}
                amount={amount}
                currency={currency?.code ?? ""}
                decimals={decimals}
              />
            ) : activeBet ? (
              <button
                type="button"
                aria-label={phase === "FLYING" ? t("bet.cashout") : t("bet.cancel")}
                className={
                  phase === "FLYING"
                    ? "cx-bet-action cx-bet-action--cashout"
                    : "cx-bet-action cx-bet-action--placed"
                }
                disabled={pending || !canBet || (phase !== "BETTING" && phase !== "FLYING")}
                onClick={() => {
                  if (phase === "BETTING") {
                    auto.stop();
                    void actions.cancel();
                  } else if (phase === "FLYING") void actions.cashout();
                }}
              >
                <strong>
                  {pending
                    ? t("bet.pending")
                    : phase === "FLYING"
                      ? t("bet.cashout")
                      : phase === "BETTING"
                        ? t("bet.cancel")
                        : t("bet.settling")}
                </strong>
                {phase === "FLYING" ? (
                  <small>
                    <LiveWin amount={activeBet.amount} decimals={decimals} /> {activeBet.currency}
                  </small>
                ) : null}
              </button>
            ) : (
              <button
                type="button"
                aria-label={t("bet.place")}
                className="cx-bet-action cx-bet-action--bet"
                disabled={pending || !canBet || phase !== "BETTING" || !valid}
                onClick={place}
              >
                <strong>
                  {pending ? t("bet.pending") : phase === "BETTING" ? t("bet.place") : t("bet.waiting")}
                </strong>
                {currency ? (
                  <small>
                    {amount} {currency.code}
                  </small>
                ) : null}
              </button>
            )}
          </div>
        </div>
        <BetFooter
          value={autoCashout}
          onChange={setAutoCashout}
          disabled={blocked}
          max={config?.maxMultiplier}
          currency={currency}
        />
        {tab === "auto" ? <AutoBetControls fields={fields} running={auto.running} /> : null}
        {latest && latest.status !== "active" && latest.status !== "pending" ? (
          <p
            role="status"
            className="m-0 text-sm"
            style={{ color: latest.status === "won" ? "var(--cx-color-success)" : "var(--cx-color-muted)" }}
          >
            {t(
              latest.status === "won" ? "bet.won" : latest.status === "lost" ? "bet.lost" : "bet.cancelled",
              {
                amount: new Decimal(latest.status === "won" ? (latest.win ?? 0) : latest.amount).toFixed(
                  decimals,
                ),
                currency: latest.currency,
                multiplier: latest.multiplier?.toFixed(2),
              },
            )}
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-danger" role="alert">
            {t("errors." + error)}
          </p>
        ) : null}
        {!valid && currency && !blocked ? (
          <p className="m-0 text-xs text-warning">{t("bet.invalid")}</p>
        ) : null}
      </Panel>
    </section>
  );
}
